// apps/client/src/ui/screens/admin.tsx (M2-T08, spec §10.1) — the moderation page: open and resolved reports with the server's content
// snapshot, the reporter's note, and Dismiss / Mark actioned. Listed in the Menu only for admins (`me.isAdmin`).
import type { AdminReport } from '@whiskerton/shared';
import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../../api/endpoints';
import { HttpError } from '../../api/http';
import { useGame } from '../../state/store';
import { useT } from '../i18n';
import { Panel, Tabs } from '../kit';
import type { ScreenDef } from '../router';
import { quoteStyle, SafetyButton } from './report';

declare module '../router' { interface ScreenParams { admin: undefined } }

type Status = 'open' | 'resolved';
const failed = (e: unknown) => useGame.getState().toast(e instanceof HttpError ? (`errors.${e.error}` as const) : 'errors.server_error', undefined, 'warn');
const KIND_LABEL = { chat: 'safety.kindChat', letter: 'safety.kindLetter', name: 'safety.kindName', town_name: 'safety.kindTownName' } as const;
const RESOLUTION_LABEL: Readonly<Record<string, 'safety.adminDismissed' | 'safety.adminActioned'>> = { dismissed: 'safety.adminDismissed', actioned: 'safety.adminActioned' };

function Row({ r, resolve, busy }: { r: AdminReport; resolve: (resolution: 'dismissed' | 'actioned') => void; busy: boolean }) {
  const t = useT();
  return (
    <li data-testid="admin-report" style={{ display: 'grid', gap: 8, padding: '12px 14px', borderRadius: 16, background: 'var(--cream-100)' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'baseline', justifyContent: 'space-between' }}>
        <strong style={{ fontWeight: 900 }}>{t(KIND_LABEL[r.kind])}</strong>
        <span style={{ fontWeight: 800 }}>{t('safety.adminTarget', { name: r.targetName || t('common.formerResident'), town: r.townName })}</span>
      </div>
      <blockquote style={{ ...quoteStyle, background: 'var(--cream-50)' }}>{r.content}</blockquote>
      {r.note && <p style={{ margin: 0, fontStyle: 'italic' }}>{r.note}</p>}
      <p style={{ margin: 0, fontSize: 14 }}>{t('safety.adminFrom', { name: r.reporterName ?? t('common.formerResident') })}</p>
      {r.status === 'open' ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          <SafetyButton testId="admin-dismiss" variant="quiet" disabled={busy} onClick={() => resolve('dismissed')}>{t('safety.adminDismiss')}</SafetyButton>
          <SafetyButton testId="admin-action" disabled={busy} onClick={() => resolve('actioned')}>{t('safety.adminAction')}</SafetyButton>
        </div>
      ) : r.resolution && RESOLUTION_LABEL[r.resolution] && <p style={{ margin: 0, fontSize: 14, fontWeight: 800 }}>{t(RESOLUTION_LABEL[r.resolution]!)}</p>}
    </li>
  );
}

function Admin({ close }: { params: undefined; close: () => void }) {
  const t = useT();
  const [status, setStatus] = useState<Status>('open');
  const [reports, setReports] = useState<AdminReport[] | null>(null);
  const [busy, setBusy] = useState(false);
  // Only the newest request may fill the list: a slow answer for the tab you just left (or an older reload) is dropped.
  const shown = useRef<Status>(status);
  const latest = useRef(0);
  const load = useCallback(async (st: Status) => {
    const req = ++latest.current;
    try { const { reports: list } = await api.adminReports(st); if (req === latest.current) setReports(list); }
    catch (e) { if (req === latest.current) { setReports([]); failed(e); } }
  }, []);
  useEffect(() => { shown.current = status; setReports(null); void load(status); }, [status, load]);
  const resolve = async (id: string, resolution: 'dismissed' | 'actioned') => {
    setBusy(true);
    try { await api.resolveReport(id, { resolution }); }
    catch (e) { failed(e); }
    finally { setBusy(false); }
    await load(shown.current);                                        // the tab showing now, not the one it was clicked on
  };
  return (
    <Panel title={t('safety.adminTitle')}>
      <div style={{ display: 'grid', gap: 12, width: 'min(620px, 82cqw)', maxWidth: '100%' }}>
        <Tabs tabs={[{ id: 'open', label: t('safety.adminOpen') }, { id: 'resolved', label: t('safety.adminResolved') }]} active={status}
          onChange={(id) => setStatus(id as Status)} />
        {reports === null ? <p style={{ margin: 0 }}>{t('common.loading')}</p>
          : reports.length === 0 ? <p style={{ margin: 0 }}>{t('safety.adminEmpty')}</p> : (
            <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 10 }}>
              {reports.map((r) => <Row key={r.id} r={r} busy={busy} resolve={(res) => void resolve(r.id, res)} />)}
            </ul>
          )}
        <SafetyButton variant="quiet" onClick={close}>{t('common.close')}</SafetyButton>
      </div>
    </Panel>
  );
}
export const screen: ScreenDef<'admin'> = {
  id: 'admin', layer: 'panel', component: Admin, menu: { label: 'safety.adminTitle', order: 99, visible: (s) => !!s.me?.isAdmin },
};
